import { createApiClient, apiClient } from '../../../../api/apiClient';

const api = createApiClient();
const BASE = '/analytics/crewing';

const post = <T>(path: string, body: CrewingFilterRequest): Promise<T> =>
  apiClient(api, `${BASE}${path}`, 'POST', body) as Promise<T>;

// ─── Refresh status ─────────────────────────────────────────────────────────

export interface CrewingRefreshStatusResponse {
  lastRefreshedAt:    string;
  lastRefreshedLabel: string;
}

export const fetchCrewingRefreshStatus = (): Promise<CrewingRefreshStatusResponse> =>
  apiClient(api, `${BASE}/refresh-status`, 'GET') as Promise<CrewingRefreshStatusResponse>;

// ─── Request ──────────────────────────────────────────────────────────────────

export interface CrewingFilterRequest {
  regions?:         string[];
  markets?:         string[];
  locations?:       string[];
  years?:           number[];
  periods?:         number[];
  weeks?:           number[];
  zones?:           string[];
  categories?:      string[];
  groupBy?:         'region' | 'market' | 'location';
  timeGrain?:       'year' | 'period' | 'week' | 'day';
  groupDimensions?: string[];
  page?:            number;
  pageSize?:        number;
  // Server-side column sort for the 4 paginated grouped-table endpoints only — case-insensitive and
  // fails open (an unrecognized sortBy is silently ignored, falling back to default grouping order).
  sortBy?:          string;
  sortDirection?:   'asc' | 'desc';
}

// ─── Response types ───────────────────────────────────────────────────────────

export interface CrewingKpiMetric {
  name:           string;
  value:          number;
  target:         number;
  delta:          number;
  format:         'percent' | 'decimal';
  trendDirection: 'up' | 'down' | 'flat';
}

export interface ScheduleHealthRow {
  label:      string;
  accuracy:   number;
  efficiency: number;
  compliance: number;
  kcphIndex:  number;
}

export interface SchedulingTrendResponse {
  periods:    string[];
  accuracy:   number[];
  efficiency: number[];
  compliance: number[];
  kcphIndex:  number[];
}

export interface KcphPerformanceRow {
  label:     string;
  estimated: number;
  scheduled: number;
  actual:    number;
}

export interface KcphPerformanceResponse {
  rows:   KcphPerformanceRow[];
  // Single KCPH target for the whole filtered scope — optional so existing responses without it still work.
  target?: number;
}

export interface ToolAdoptionRow {
  label:  string;
  value:  number;
  target: number;
}

export interface VarianceHeatmapResponse {
  zones: string[];
  days:  string[];
  cells: number[][];
  meta?: { dataSource?: string; note?: string };
}

export interface KcphTrendResponse {
  labels:    string[];
  estimated: number[];
  scheduled: number[];
  actual:    number[];
  // A single number is a flat target; an array (one value per label/period) allows the
  // target to vary by period.
  target:    number | number[];
}

export interface LocationEfficiencyRow {
  region:            string;
  market:            string;
  location:          string;
  // Location_ID — required by /efficiency/employee-table's `locations` filter (the display
  // `location` name never matches a Schedule row there). Present whenever 'location' is part
  // of the resolved groupDimensions.
  locationId?:       string;
  year:              string;
  period:            string;
  week:              string;
  category:          string;
  zone:              string;
  forecastedCases:   number;
  actualCases:       number | null;
  forecastedHours:   number;
  scheduleHours:     number;
  estimatedHours:    number;
  actualHours:       number;
  siteStandardKcph:  number;
  scheduledKcph:     number;
  actualKcph:        number;
  targetKcph:        number;
  kcphIndex:         number;
}

export interface EmployeeEfficiencyRow {
  location:      string;
  employeeName:  string;
  employeeGpid:  string;
  role:          string;
  year:          string;
  period:        string;
  week:          string;
  category:      string;
  zone:          string;
  estimatedKcph: number;
  scheduledKcph: number;
  actualKcph:    number;
}

export interface HoursGroup {
  label:  string;
  values: {
    plmRecommended: number;
    forecasted:     number;
    scheduled:      number;
    actual:         number;
  };
}

export interface HoursCategoryRow {
  region:          string;
  market:          string;
  location:        string;
  year:            string;
  period:          string;
  week:            string;
  category:        string;
  zone:            string;
  scheduleHours:   number;
  actualHours:     number;
  forecastedHours: number | null;
  estimatedHours:  number | null;
  size:            string | null;
  actualCases:     number | null;
  routes:          number | null;
  siteSqft:        number | null;
}

export interface AdoptionFunnelResponse {
  eligible:   number;
  active:     number;
  submitted:  number;
  maintained: number;
  meta?: { dataSource?: string; note?: string };
}

export interface QuadrantPoint {
  label:       string;
  adoptionPct: number;
  // null when the group has no Actual_Cases/hours data for kcphIndex's underlying ratio this scope.
  kcphIndex:   number | null;
  withTool:    boolean;
  // Case/route volume backing the bubble's size — optional since older backend responses may omit it.
  volume?:     number | null;
}

export interface AdoptionTrendResponse {
  labels:       string[];
  kcphIndexPct: number[];
  adoptionPct:  number[];
}

export interface QualityMetricsRow {
  region:            string;
  market:            string;
  location:          string;
  year:              string;
  period:            string;
  week:              string;
  category:          string;
  zone:              string;
  forecastedHours:   number;
  scheduledHours:    number;
  estimatedHours:    number;
  // Null when the row has no final Kronos actual yet — Accuracy/Efficiency (both actual-based)
  // are then also null, while Compliance (forecast/scheduled only) is still populated.
  actualHours:       number | null;
  crewingAccuracy:   number | null;
  crewingEfficiency: number | null;
  crewingCompliance: number;
}

export interface SubmissionDetailsRow {
  region:            string;
  market:            string;
  location:          string;
  year:              string;
  period:            string;
  week:              string;
  submissions:       number;
  modificationCount: number;
  lastSubmitted:     string;
  lastModified:      string;
}

export interface PagedResponse<T> {
  total: number;
  rows:  T[];
  // Client-side only: set by useCrewingAnalytics' streaming pagination (employeeTable only) —
  // false while more pages are still loading in the background, true once every row has arrived.
  complete?: boolean;
}

// `totals` (per backend contract) is aggregated across the FULL filtered result set, independent
// of `page`/`pageSize` — the UI must render these directly instead of summing the current page's rows.
export interface LocationTableTotals {
  forecastedCases:  number;
  actualCases:      number | null;
  forecastedHours:  number;
  scheduleHours:    number;
  estimatedHours:   number;
  actualHours:      number;
  siteStandardKcph: number;
  scheduledKcph:    number;
  actualKcph:       number;
  targetKcph:       number;
  kcphIndex:        number;
}

export interface LocationTableResponse extends PagedResponse<LocationEfficiencyRow> {
  totals: LocationTableTotals;
}

export interface HoursCategoryTotals {
  scheduleHours:   number;
  actualHours:     number;
  forecastedHours: number | null;
  estimatedHours:  number | null;
  actualCases:     number | null;
  routes:          number | null;
  siteSqft:        number | null;
}

export interface HoursCategoryTableResponse extends PagedResponse<HoursCategoryRow> {
  totals: HoursCategoryTotals;
  meta?: { note?: string };
}

export interface QualityMetricsTotals {
  forecastedHours:   number;
  scheduledHours:    number;
  estimatedHours:    number;
  actualHours:       number | null;
  crewingAccuracy:   number | null;
  crewingEfficiency: number | null;
  crewingCompliance: number;
}

export interface QualityMetricsTableResponse extends PagedResponse<QualityMetricsRow> {
  totals: QualityMetricsTotals;
}

export interface SubmissionDetailsTotals {
  submissions:       number;
  modificationCount: number;
}

export interface SubmissionDetailsResponse extends PagedResponse<SubmissionDetailsRow> {
  totalModificationCount: number;
  totals: SubmissionDetailsTotals;
}

// ─── API functions ─────────────────────────────────────────────────────────────

export const fetchCrewingKpiOverview = (req: CrewingFilterRequest): Promise<{ metrics: CrewingKpiMetric[] }> =>
  post('/kpi-overview', req);

export const fetchCrewingScheduleHealth = (req: CrewingFilterRequest): Promise<{ rows: ScheduleHealthRow[] }> =>
  post('/schedule-health', req);

export const fetchCrewingSchedulingTrend = (req: CrewingFilterRequest): Promise<SchedulingTrendResponse> =>
  post('/scheduling-trend', req);

export const fetchCrewingKcphPerformance = (req: CrewingFilterRequest): Promise<KcphPerformanceResponse> =>
  post('/kcph-performance', req);

export const fetchCrewingToolAdoption = (req: CrewingFilterRequest): Promise<{ rows: ToolAdoptionRow[] }> =>
  post('/tool-adoption', req);

export const fetchCrewingVarianceHeatmap = (req: CrewingFilterRequest): Promise<VarianceHeatmapResponse> =>
  post('/efficiency/variance-heatmap', req);

export const fetchCrewingKcphTrend = (req: CrewingFilterRequest): Promise<KcphTrendResponse> =>
  post('/efficiency/kcph-trend', req);

export const fetchCrewingLocationTable = (req: CrewingFilterRequest): Promise<LocationTableResponse> =>
  post('/efficiency/location-table', req);

export const fetchCrewingEmployeeTable = (req: CrewingFilterRequest): Promise<PagedResponse<EmployeeEfficiencyRow> & { meta?: object }> =>
  post('/efficiency/employee-table', req);

export const fetchCrewingDirectIndirectHours = (req: CrewingFilterRequest): Promise<{ groups: HoursGroup[] }> =>
  post('/hours/direct-indirect', req);

export const fetchCrewingOtherFixedHours = (req: CrewingFilterRequest): Promise<{ groups: HoursGroup[] }> =>
  post('/hours/other-fixed', req);

export const fetchCrewingHoursCategoryTable = (req: CrewingFilterRequest): Promise<HoursCategoryTableResponse> =>
  post('/hours/category-table', req);

export const fetchCrewingAdoptionFunnel = (req: CrewingFilterRequest): Promise<AdoptionFunnelResponse> =>
  post('/execution/adoption-funnel', req);

export const fetchCrewingQuadrant = (req: CrewingFilterRequest): Promise<{ points: QuadrantPoint[] }> =>
  post('/execution/quadrant', req);

export const fetchCrewingAdoptionTrend = (req: CrewingFilterRequest): Promise<AdoptionTrendResponse> =>
  post('/execution/adoption-trend', req);

export const fetchCrewingQualityMetrics = (req: CrewingFilterRequest): Promise<QualityMetricsTableResponse> =>
  post('/execution/quality-metrics', req);

export const fetchCrewingSubmissionDetails = (req: CrewingFilterRequest): Promise<SubmissionDetailsResponse> =>
  post('/execution/submission-details', req);
